// Resource Gantt — d3 스케일 / 스택 계산 (d3는 "계산만" 담당)
//
// 그리기(fillRect 등)는 ResourceGanttCanvas가 맡고, 여기서는
// 버킷 x좌표 / 용량 y좌표 / 바닥부터의 누적 스택만 만든다.

import { scaleLinear, scaleOrdinal, schemeTableau10 } from "d3";

// 용량(0..laneRef) → y 픽셀. 위로 갈수록 값이 커지도록 range를 뒤집는다.
//   y(0) = rowBottom, y(laneRef) = rowTop
// clamp(true): 여유(laneRef)를 넘는 초과분은 끝(rowTop)에 고정 → 행 밖으로 안 넘침.
export const buildCapacityScale = (laneRef, rowTop, rowBottom) =>
  scaleLinear().domain([0, laneRef || 1]).range([rowBottom, rowTop]).clamp(true);

// 버킷 총능력 / 부하합 — 응답(버킷) 필드 접근을 한 곳으로 모은다.
export const bucketCap = (b) => b?.totalCapacity ?? 0;
export const bucketUsed = (b) => (b?.items || []).reduce((acc, it) => acc + (it.capacity || 0), 0);

// 사용 한도 비율(z_resource_capacity.usage_limit_rate) — 미설정이면 1(=100%).
export const bucketLimitRate = (b) => (b?.usageLimitRate != null ? b.usageLimitRate : 1);
// 한계 용량 = 총능력 × 사용 한도 비율. '초과 판정'의 기준선이다.
// 총능력(bucketCap)은 계획 엔진이 한계로 쓰는 값(z_resource_util.capa_total)이라 표시 숫자로 그대로 두고,
// 초과 여부와 색만 이 값으로 판단한다. usageLimitRate 는 계획 엔진에 반영되지 않는 표시용 값이다.
export const bucketLimitCap = (b) => bucketCap(b) * bucketLimitRate(b);

// 백엔드는 데이터 없는 버킷을 응답에서 생략한다(ResourceGanttService.buildGantt).
// 간트 x축(bucketKeys)과 열을 맞춰야 하는 화면(포커스 차트/그리드)은 빈 버킷을 끼워 정렬한다.
export const alignBuckets = (buckets = [], bucketKeys = []) => {
  if (!bucketKeys.length) return buckets;
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  return bucketKeys.map((key) => byKey.get(key) ?? { key, totalCapacity: 0, items: [] });
};

// 버킷 items를 바닥부터 누적 → [{ item, capacity, setupCapacity, y0, y1 }]
// y1 = y0 + capacity (전체 점유 범위). capacity 는 setupCapacity 를 이미 포함한 값이므로
// 둘을 더하지 않는다. setup 구간은 [y0, y0 + setupCapacity] 로 세그먼트 '안쪽'에 표시한다.
// d3.stack은 희소(sparse) 버킷에 과하므로 수동 누적이 더 단순/빠르다.
export const stackBucket = (items = []) => {
  let acc = 0;
  return items.map((it) => {
    const y0 = acc;
    acc += it.capacity || 0;
    return { ...it, y0, y1: acc };
  });
};

// 스택 합계(= 마지막 세그먼트의 y1).
export const stackTotal = (stacked) =>
  stacked.length ? stacked[stacked.length - 1].y1 : 0;

// cap 위(또는 우)로 항상 두는 여유 비율 — 초과 표시 공간 + cap 라인 위치 일관성용.
export const LANE_HEADROOM = 0.25;

// 레인(리소스) 세로/가로 스케일 기준값.
// 초과 유무와 무관하게 항상 "최대 capacity × (1+여유)"로 고정한다.
// → 같은 용량이면 cap 라인 위치도 항상 동일(예: 막대의 80% 지점).
//   여유(25%)를 넘는 초과는 끝에서 잘리되(clamp), 빨간 cap 라인으로 초과를 표시한다.
// capacity는 버킷별로 다를 수 있어 레인 내 최대 capacity(또는 resource.maxCapacity)를 기준으로 잡는다.
// draw()와 hitTest()가 동일 좌표계를 쓰도록 공용 사용.
// headroom: 초과 표시 여백 비율. 기본 0.25(=LANE_HEADROOM). 0이면 100%일 때 버킷이 꽉 찬다(초과 여백 없음).
export const computeLaneRef = (buckets = [], maxCapacity = 0, headroom = LANE_HEADROOM) => {
  let maxCap = maxCapacity || 0;
  let maxLoad = 0;
  for (const b of buckets) {
    if (bucketCap(b) > maxCap) maxCap = bucketCap(b);
    if (bucketUsed(b) > maxLoad) maxLoad = bucketUsed(b);
  }
  const base = Math.max(maxCap, maxLoad);
  return (base || 1) * (1 + headroom);
};

// 아이템 → 색 (카테고리컬). 전체 아이템 집합으로 도메인을 고정해 색을 안정화.
// colors: 팔레트 색 배열(없으면 d3 기본). 같은 아이템이면 팔레트가 같은 한 같은 색.
export const buildItemColor = (bucketsByResource, colors) => {
  const scale = scaleOrdinal(colors ?? schemeTableau10);
  const seen = new Set();
  Object.values(bucketsByResource).forEach((buckets) =>
    buckets.forEach((b) => (b.items || []).forEach((it) => seen.add(it.item)))
  );
  scale.domain([...seen]);
  return (item) => scale(item);
};
